import { Activity, BarChart3, CircleHelp, HeartPulse, Layers3, Sparkles } from "lucide-react";
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, Area, ComposedChart, LabelList } from "recharts";
import { Card } from "@/components/ui/card";

type ChartRow = Record<string, unknown>;

type ExecutiveBlock = {
  eligible?: Record<string, unknown> | null;
  risk?: Record<string, unknown> | null;
  activeSearch?: Record<string, unknown> | null;
  monitoring?: Record<string, unknown> | null;
  experience?: Record<string, unknown> | null;
};

type ApsDashboardData = {
  executive?: ExecutiveBlock | null;
  population?: {
    sexMix?: ChartRow[];
    ageBands?: ChartRow[];
    ageSexBands?: ChartRow[];
  } | null;
  production?: {
    monthlySeries?: ChartRow[];
    funnel?: ChartRow[];
    specialties?: ChartRow[];
  } | null;
  activeSearch?: {
    channels?: ChartRow[];
    campaigns?: ChartRow[];
  } | null;
  monitoring?: {
    outcomes?: ChartRow[];
    hospitals?: ChartRow[];
  } | null;
  populationHealth?: {
    riskDistribution?: ChartRow[];
    lifestyle?: ChartRow[];
    screenings?: ChartRow[];
    careLines?: ChartRow[];
  } | null;
  experience?: {
    distribution?: ChartRow[];
    themes?: ChartRow[];
    testimonials?: ChartRow[];
  } | null;
  collectiveActions?: {
    monthlySeries?: ChartRow[];
    channels?: ChartRow[];
  } | null;
};

type ApsModulePanelsProps = {
  activeSectionId: string;
  data: ApsDashboardData | null | undefined;
  accentColor: string;
  moduleLabel: string;
  isLoading?: boolean;
};

const PIE_COLORS = ["#0f172a", "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

function toNumber(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toLabel(value: unknown, fallback = "Sem rótulo") {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number") return String(value);
  return fallback;
}

function formatInteger(value: unknown) {
  return toNumber(value).toLocaleString("pt-BR");
}

function formatDecimal(value: unknown, digits = 1) {
  return toNumber(value).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function summarizePrimarySeries(data: Array<{ chartLabel: string; chartValue: number }>) {
  if (data.length === 0) return "Série pronta para leitura assim que a base consolidar mais pontos.";
  const peak = data.reduce((best, item) => (item.chartValue > best.chartValue ? item : best), data[0]);
  const last = data[data.length - 1];
  return `Pico recente em ${peak.chartLabel} com ${formatInteger(peak.chartValue)} registros. Última leitura em ${last.chartLabel}: ${formatInteger(last.chartValue)}.`;
}

function summarizeComparativeSeries(data: Array<{ chartLabel: string; chartValue: number; secondaryValue: number }>, secondaryKey?: string) {
  if (data.length === 0) return "A comparação aparecerá aqui quando houver massa crítica suficiente no período.";
  const peak = data.reduce((best, item) => (item.chartValue > best.chartValue ? item : best), data[0]);
  if (!secondaryKey) {
    return `Maior intensidade observada em ${peak.chartLabel}, com ${formatInteger(peak.chartValue)} ocorrências no recorte.`;
  }
  return `Em ${peak.chartLabel}, o principal alcançou ${formatInteger(peak.chartValue)} e o complementar ${formatInteger(peak.secondaryValue)}, reforçando a leitura comparativa do bloco.`;
}

function summarizePieSeries(data: Array<{ name: string; value: number }>) {
  if (data.length === 0) return "A composição aparecerá aqui quando houver distribuição válida no período.";
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const leading = data.reduce((best, item) => (item.value > best.value ? item : best), data[0]);
  const share = total > 0 ? (leading.value / total) * 100 : 0;
  return `${leading.name} lidera esta distribuição com ${formatDecimal(share, 1)}% do total observado.`;
}

function formatPercent(value: number, digits = 1) {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function buildSafeId(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function ExecutiveAreaTrendChart({
  title,
  description,
  data,
  labelKey,
  primaryKey,
  primaryLabel,
  secondaryKey,
  secondaryLabel,
  accentColor,
  secondaryColor = "#0f172a",
}: {
  title: string;
  description: string;
  data: ChartRow[];
  labelKey: string;
  primaryKey: string;
  primaryLabel: string;
  secondaryKey?: string;
  secondaryLabel?: string;
  accentColor: string;
  secondaryColor?: string;
}) {
  const normalizedData = data.map((row) => ({
    chartLabel: toLabel(row[labelKey]),
    primaryValue: toNumber(row[primaryKey]),
    secondaryValue: secondaryKey ? toNumber(row[secondaryKey]) : 0,
  }));

  if (!normalizedData.some((row) => row.primaryValue > 0 || row.secondaryValue > 0)) {
    return <EmptyPanel title={title} description={description} />;
  }

  const peak = normalizedData.reduce((best, item) => (item.primaryValue > best.primaryValue ? item : best), normalizedData[0]);
  const gradientId = `${buildSafeId(title)}-trend`;
  const insight = secondaryKey && secondaryLabel
    ? `${peak.chartLabel} concentrou ${formatInteger(peak.primaryValue)} em ${primaryLabel.toLowerCase()} e ${formatInteger(peak.secondaryValue)} em ${secondaryLabel.toLowerCase()}.`
    : `Pico recente em ${peak.chartLabel} com ${formatInteger(peak.primaryValue)} registros no eixo principal.`;

  return (
    <ChartShell title={title} description={description} insight={insight} accentColor={accentColor}>
      <div className="h-[288px] w-full rounded-[1.35rem] bg-[radial-gradient(circle_at_top_left,rgba(192,255,0,0.16),transparent_26%),linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={normalizedData}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={accentColor} stopOpacity={0.26} />
                <stop offset="95%" stopColor={accentColor} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="chartLabel" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
              formatter={(value: number, name: string) => [formatInteger(value), name]}
            />
            <Area type="monotone" dataKey="primaryValue" name={primaryLabel} stroke={accentColor} fill={`url(#${gradientId})`} strokeWidth={3} />
            {secondaryKey && secondaryLabel ? (
              <Line type="monotone" dataKey="secondaryValue" name={secondaryLabel} stroke={secondaryColor} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function AgePyramidPanel({
  title,
  description,
  ageBands,
  ageSexBands,
  sexMix,
  accentColor,
}: {
  title: string;
  description: string;
  ageBands: ChartRow[];
  ageSexBands: ChartRow[];
  sexMix: ChartRow[];
  accentColor: string;
}) {
  const normalizeSexLabel = (value: unknown) => {
    const normalized = toLabel(value, "Não informado").trim().toLowerCase();
    if (["f", "feminino", "female", "mulher", "women"].includes(normalized)) return "Feminino";
    if (["m", "masculino", "male", "homem", "men"].includes(normalized)) return "Masculino";
    return "Não informado";
  };

  const ageBandOrder = (label: string) => {
    const firstNumber = Number(label.match(/\d+/)?.[0] ?? 999);
    return Number.isFinite(firstNumber) ? firstNumber : 999;
  };

  const normalizedBands = ageBands
    .map((row) => ({
      ageBand: toLabel(row.ageBand),
      totalLives: toNumber(row.totalLives),
    }))
    .filter((row) => row.totalLives > 0)
    .sort((left, right) => ageBandOrder(left.ageBand) - ageBandOrder(right.ageBand));

  const normalizedAgeSexBands = ageSexBands
    .map((row) => ({
      ageBand: toLabel(row.ageBand),
      sexLabel: normalizeSexLabel(row.sexLabel),
      totalLives: toNumber(row.totalLives),
    }))
    .filter((row) => row.totalLives > 0)
    .sort((left, right) => ageBandOrder(left.ageBand) - ageBandOrder(right.ageBand));

  const aggregatedBandMap = normalizedAgeSexBands.reduce((map, row) => {
    const current = map.get(row.ageBand) ?? {
      ageBand: row.ageBand,
      femaleLives: 0,
      maleLives: 0,
      otherLives: 0,
      totalLives: 0,
    };

    if (row.sexLabel === "Feminino") current.femaleLives += row.totalLives;
    else if (row.sexLabel === "Masculino") current.maleLives += row.totalLives;
    else current.otherLives += row.totalLives;

    current.totalLives += row.totalLives;
    map.set(row.ageBand, current);
    return map;
  }, new Map<string, { ageBand: string; femaleLives: number; maleLives: number; otherLives: number; totalLives: number }>());

  const mirroredBands = Array.from(aggregatedBandMap.values()).sort((left, right) => ageBandOrder(left.ageBand) - ageBandOrder(right.ageBand));
  const baseBands = mirroredBands.length
    ? mirroredBands
    : normalizedBands.map((row) => ({
        ageBand: row.ageBand,
        femaleLives: 0,
        maleLives: row.totalLives,
        otherLives: 0,
        totalLives: row.totalLives,
      }));

  if (baseBands.length === 0) {
    return <EmptyPanel title={title} description={description} />;
  }

  const totalLives = baseBands.reduce((sum, row) => sum + row.totalLives, 0);
  const dominantBand = baseBands.reduce((best, row) => (row.totalLives > best.totalLives ? row : best), baseBands[0]);
  const gradientId = `${buildSafeId(title)}-age`;
  const mirroredRows = baseBands.map((row) => ({
    ...row,
    femaleValue: row.femaleLives * -1,
    maleValue: row.maleLives,
    otherValue: row.otherLives,
    shareLabel: `${formatPercent(totalLives > 0 ? (row.totalLives / totalLives) * 100 : 0, 1)}%`,
  }));

  const normalizedSexMix = (sexMix.length ? sexMix : normalizedAgeSexBands)
    .map((row) => ({
      label: normalizeSexLabel((row as Record<string, unknown>).sexLabel),
      value: toNumber((row as Record<string, unknown>).totalLives),
    }))
    .filter((row) => row.value > 0)
    .reduce((acc, row) => {
      const existing = acc.find((item) => item.label === row.label);
      if (existing) existing.value += row.value;
      else acc.push({ ...row });
      return acc;
    }, [] as Array<{ label: string; value: number }>);

  const maxSexValue = Math.max(...normalizedSexMix.map((row) => row.value), 1);
  const maxMirrorValue = Math.max(...mirroredRows.map((row) => Math.max(row.femaleLives, row.maleLives, row.otherLives)), 1);
  const femaleTotal = normalizedSexMix.find((row) => row.label === "Feminino")?.value ?? 0;
  const maleTotal = normalizedSexMix.find((row) => row.label === "Masculino")?.value ?? 0;
  const balanceDelta = Math.abs(femaleTotal - maleTotal);

  return (
    <ChartShell
      title={title}
      description={description}
      insight={`${dominantBand.ageBand} concentra ${formatPercent(totalLives > 0 ? (dominantBand.totalLives / totalLives) * 100 : 0, 1)}% da carteira observada, com leitura espelhada por sexo para comparar a densidade etária sem perder rapidez executiva.`}
      accentColor={accentColor}
      footer={
        normalizedSexMix.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {normalizedSexMix.map((row, index) => (
              <div key={row.label} className="rounded-[1.1rem] border border-slate-100 bg-slate-50 px-3.5 py-3">
                <div className="flex items-center justify-between gap-3 text-sm font-medium text-slate-700">
                  <span>{row.label}</span>
                  <span className="font-semibold text-slate-950">{formatInteger(row.value)}</span>
                </div>
                <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max((row.value / maxSexValue) * 100, 12)}%`,
                      background: index === 0 ? accentColor : "#0f172a",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="grid gap-4 xl:grid-cols-[1.45fr_0.78fr]">
        <div className="h-[320px] w-full rounded-[1.35rem] bg-[linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mirroredRows} layout="vertical" margin={{ top: 8, right: 20, left: 20, bottom: 8 }} barCategoryGap={10}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0f172a" stopOpacity={0.98} />
                  <stop offset="100%" stopColor={accentColor} stopOpacity={0.9} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis
                type="number"
                domain={[-maxMirrorValue, maxMirrorValue]}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: 12 }}
                tickFormatter={(value) => formatInteger(Math.abs(toNumber(value)))}
              />
              <YAxis type="category" dataKey="ageBand" width={72} tickLine={false} axisLine={false} tick={{ fill: "#475569", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  borderRadius: "1rem",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
                }}
                formatter={(value: number, name: string) => [formatInteger(Math.abs(toNumber(value))), name === "femaleValue" ? "Feminino" : name === "maleValue" ? "Masculino" : "Não informado"]}
              />
              <Bar dataKey="femaleValue" name="femaleValue" fill="#0f172a" radius={[12, 0, 0, 12]} />
              <Bar dataKey="maleValue" name="maleValue" fill={`url(#${gradientId})`} radius={[0, 12, 12, 0]}>
                <LabelList dataKey="shareLabel" position="right" fill="#475569" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid gap-3 content-start">
          <div className="rounded-[1.25rem] border border-slate-200 bg-[linear-gradient(135deg,rgba(15,23,42,0.98)_0%,rgba(15,23,42,0.88)_65%,rgba(192,255,0,0.18)_100%)] px-4 py-4 text-white shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">Base analisada</p>
            <p className="mt-2 text-3xl font-bold">{formatInteger(totalLives)}</p>
            <p className="mt-2 text-sm leading-6 text-white/78">Leitura demográfica em barras espelhadas para mostrar densidade etária e equilíbrio entre perfis sem alongar a dobra.</p>
          </div>
          <div className="rounded-[1.25rem] border border-slate-200 bg-white px-4 py-4 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Faixa dominante</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">{dominantBand.ageBand}</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">{formatInteger(dominantBand.totalLives)} vidas, equivalentes a {formatPercent(totalLives > 0 ? (dominantBand.totalLives / totalLives) * 100 : 0, 1)}% do recorte.</p>
          </div>
          <div className="rounded-[1.25rem] border border-slate-200 bg-white px-4 py-4 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Equilíbrio por sexo</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">Δ {formatInteger(balanceDelta)}</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">{formatInteger(femaleTotal)} feminino(s) versus {formatInteger(maleTotal)} masculino(s), com eixo central único para comparação imediata.</p>
          </div>
        </div>
      </div>
    </ChartShell>
  );
}

function FunnelJourneyPanel({
  title,
  description,
  data,
  labelKey,
  valueKey,
  accentColor,
}: {
  title: string;
  description: string;
  data: ChartRow[];
  labelKey: string;
  valueKey: string;
  accentColor: string;
}) {
  const normalizedData = data
    .map((row) => ({
      label: toLabel(row[labelKey]),
      value: toNumber(row[valueKey]),
    }))
    .filter((row) => row.value > 0);

  if (normalizedData.length === 0) {
    return <EmptyPanel title={title} description={description} />;
  }

  const baseline = normalizedData[0]?.value || 1;
  const last = normalizedData[normalizedData.length - 1];
  const insight = `${last.label} fecha o percurso com ${formatInteger(last.value)} registros, equivalente a ${formatPercent((last.value / baseline) * 100, 1)}% da etapa inicial.`;

  return (
    <ChartShell title={title} description={description} insight={insight} accentColor={accentColor}>
      <div className="grid gap-3">
        {normalizedData.map((row, index) => {
          const share = baseline > 0 ? (row.value / baseline) * 100 : 0;
          return (
            <div key={row.label} className="rounded-[1.25rem] border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-slate-950" style={{ backgroundColor: `${accentColor}22` }}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-950">{row.label}</p>
                    <p className="text-xs text-slate-500">{formatPercent(share, 1)}% da etapa inicial</p>
                  </div>
                </div>
                <p className="text-base font-bold text-slate-950">{formatInteger(row.value)}</p>
              </div>
              <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(share, 8)}%`,
                    background: `linear-gradient(90deg, ${accentColor} 0%, rgba(15,23,42,0.88) 100%)`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </ChartShell>
  );
}

function SpecialtyRankingPanel({
  title,
  description,
  data,
  labelKey,
  dataKey,
  secondaryKey,
  accentColor,
}: {
  title: string;
  description: string;
  data: ChartRow[];
  labelKey: string;
  dataKey: string;
  secondaryKey?: string;
  accentColor: string;
}) {
  const normalizedData = data
    .map((row) => ({
      chartLabel: toLabel(row[labelKey]),
      chartValue: toNumber(row[dataKey]),
      secondaryValue: secondaryKey ? toNumber(row[secondaryKey]) : 0,
    }))
    .filter((row) => row.chartValue > 0 || row.secondaryValue > 0)
    .sort((a, b) => b.chartValue - a.chartValue)
    .slice(0, 6)
    .reverse();

  if (normalizedData.length === 0) {
    return <EmptyPanel title={title} description={description} />;
  }

  const leader = normalizedData[normalizedData.length - 1];
  const insight = secondaryKey
    ? `${leader.chartLabel} lidera com ${formatInteger(leader.chartValue)} registros e ${formatInteger(leader.secondaryValue)} no eixo complementar.`
    : `${leader.chartLabel} lidera com ${formatInteger(leader.chartValue)} registros neste recorte.`;

  return (
    <ChartShell title={title} description={description} insight={insight} accentColor={accentColor}>
      <div className="h-[288px] w-full rounded-[1.35rem] bg-[linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={normalizedData} layout="vertical" margin={{ top: 8, right: 20, left: 20, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <YAxis type="category" dataKey="chartLabel" width={110} tickLine={false} axisLine={false} tick={{ fill: "#475569", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
              formatter={(value: number) => [formatInteger(value), "Volume"]}
            />
            <Bar dataKey="chartValue" name="Volume" fill={accentColor} radius={[0, 12, 12, 0]} />
            {secondaryKey ? <Bar dataKey="secondaryValue" name="Complementar" fill="#0f172a" radius={[0, 12, 12, 0]} /> : null}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function ChartShell({
  title,
  description,
  insight,
  accentColor,
  children,
  footer,
}: {
  title: string;
  description: string;
  insight: string;
  accentColor: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
      <div className="border-b border-slate-100 bg-[linear-gradient(180deg,rgba(248,250,252,0.98)_0%,rgba(241,245,249,0.92)_100%)] px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-white bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" style={{ color: accentColor }} />
              leitura executiva
            </div>
            <h3 className="text-base font-semibold text-slate-950">{title}</h3>
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
            title={`${description}${insight ? `\n\n${insight}` : ""}`}
            aria-label={`Mais informações sobre ${title}`}
          >
            <CircleHelp className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-5 py-4">
        {children}
        {footer ? <div className="mt-4">{footer}</div> : null}
      </div>
    </Card>
  );
}

function ExecutiveCards({ data, accentColor }: { data: ExecutiveBlock | null | undefined; accentColor: string }) {
  const cards = [
    {
      title: "Vidas elegíveis",
      value: data?.eligible?.eligibleLives,
      description: "Base ativa do módulo.",
    },
    {
      title: "Risco prioritário",
      value: data?.risk?.priorityLives,
      description: "Maior urgência clínica.",
    },
    {
      title: "Busca ativa elegível",
      value: data?.activeSearch?.eligibleSearchLives,
      description: "Público acionável.",
    },
    {
      title: "Eventos monitorados",
      value: data?.monitoring?.monitoredLives,
      description: "Eventos em acompanhamento.",
    },
    {
      title: "Pesquisas respondidas",
      value: data?.experience?.answeredSurveys,
      description: "Base válida de experiência.",
    },
    {
      title: "NPS do período",
      value: data?.experience?.npsScore,
      description: "Percepção consolidada.",
      suffix: " pts",
      decimals: 1,
    },
  ];

  const maxValue = Math.max(...cards.map((card) => toNumber(card.value)), 1);

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
      {cards.map((card, index) => {
        const value = toNumber(card.value);
        const width = Math.max((value / maxValue) * 100, 10);
        return (
          <Card key={card.title} className="overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white shadow-sm">
            <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${accentColor} 0%, rgba(15,23,42,0.82) 100%)`, width: `${width}%` }} />
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{card.title}</p>
                  <p className="mt-2 text-2xl font-bold text-slate-950" style={{ color: index % 2 === 0 ? accentColor : "#0f172a" }}>
                    {card.decimals ? `${formatDecimal(card.value, card.decimals)}${card.suffix ?? ""}` : formatInteger(card.value)}
                  </p>
                </div>
                <div className="rounded-2xl px-2.5 py-2 text-slate-500" style={{ backgroundColor: `${accentColor}12` }}>
                  <Activity className="h-4 w-4" style={{ color: accentColor }} />
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
  return (
    <Card className="rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-8 text-center shadow-sm">
      <Layers3 className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-4 text-lg font-semibold text-slate-950">{title}</p>
      <p className="mt-3 text-sm leading-7 text-slate-600">{description}</p>
    </Card>
  );
}

function MetricBarChart({
  title,
  description,
  data,
  dataKey,
  labelKey,
  secondaryKey,
  accentColor,
  secondaryColor = "#94a3b8",
}: {
  title: string;
  description: string;
  data: ChartRow[];
  dataKey: string;
  labelKey: string;
  secondaryKey?: string;
  accentColor: string;
  secondaryColor?: string;
}) {
  const normalizedData = data.map((row) => ({
    ...row,
    chartLabel: toLabel(row[labelKey]),
    chartValue: toNumber(row[dataKey]),
    secondaryValue: secondaryKey ? toNumber(row[secondaryKey]) : 0,
  }));

  if (normalizedData.length === 0) {
    return <EmptyPanel title={title} description={description} />;
  }

  return (
    <ChartShell
      title={title}
      description={description}
      insight={summarizeComparativeSeries(normalizedData, secondaryKey)}
      accentColor={accentColor}
    >
      <div className="h-[272px] w-full rounded-[1.3rem] bg-[linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={normalizedData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="chartLabel" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
            />
            <Bar dataKey="chartValue" fill={accentColor} radius={[10, 10, 4, 4]} />
            {secondaryKey ? <Bar dataKey="secondaryValue" fill={secondaryColor} radius={[10, 10, 4, 4]} /> : null}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function MetricLineChart({
  title,
  description,
  data,
  valueKey,
  labelKey,
  accentColor,
}: {
  title: string;
  description: string;
  data: ChartRow[];
  valueKey: string;
  labelKey: string;
  accentColor: string;
}) {
  const normalizedData = data.map((row) => ({
    chartLabel: toLabel(row[labelKey]),
    chartValue: toNumber(row[valueKey]),
  }));

  if (normalizedData.length === 0) {
    return <EmptyPanel title={title} description={description} />;
  }

  return (
    <ChartShell
      title={title}
      description={description}
      insight={summarizePrimarySeries(normalizedData)}
      accentColor={accentColor}
    >
      <div className="h-[272px] w-full rounded-[1.3rem] bg-[radial-gradient(circle_at_top_left,rgba(192,255,0,0.12),transparent_30%),linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={normalizedData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="chartLabel" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
            />
            <Line type="monotone" dataKey="chartValue" stroke={accentColor} strokeWidth={3.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function MetricPieChart({
  title,
  description,
  data,
  labelKey,
  valueKey,
  accentColor,
}: {
  title: string;
  description: string;
  data: ChartRow[];
  labelKey: string;
  valueKey: string;
  accentColor: string;
}) {
  const normalizedData = data.map((row) => ({
    name: toLabel(row[labelKey]),
    value: toNumber(row[valueKey]),
  })).filter((row) => row.value > 0);

  if (normalizedData.length === 0) {
    return <EmptyPanel title={title} description={description} />;
  }

  return (
    <ChartShell
      title={title}
      description={description}
      insight={summarizePieSeries(normalizedData)}
      accentColor={accentColor}
      footer={
        <div className="grid gap-3 md:grid-cols-2">
          {normalizedData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2.5">
              <div className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                <span className="text-sm font-medium text-slate-700">{item.name}</span>
              </div>
              <span className="text-sm font-semibold text-slate-950">{formatInteger(item.value)}</span>
            </div>
          ))}
        </div>
      }
    >
      <div className="h-[272px] w-full rounded-[1.3rem] bg-[linear-gradient(180deg,rgba(248,250,252,0.95)_0%,rgba(255,255,255,1)_100%)] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              contentStyle={{
                borderRadius: "1rem",
                border: "1px solid #e2e8f0",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
              }}
            />
            <Pie data={normalizedData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={110} paddingAngle={4}>
              {normalizedData.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
}

function TestimonialList({ testimonials }: { testimonials: ChartRow[] }) {
  if (testimonials.length === 0) {
    return <EmptyPanel title="Depoimentos recentes" description="Os comentários qualitativos respondidos aparecerão aqui conforme houver base suficiente no período selecionado." />;
  }

  return (
    <Card className="rounded-[1.6rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-slate-950">
        <Sparkles className="h-4.5 w-4.5 text-emerald-500" />
        <h3 className="text-base font-semibold">Depoimentos recentes</h3>
      </div>
      <p className="mt-1.5 text-sm leading-6 text-slate-600">Leitura rápida da voz do beneficiário com tema e sentimento já classificados para uso analítico.</p>
      <div className="mt-4 space-y-3">
        {testimonials.map((row, index) => (
          <div key={`${toLabel(row.freeTextComment)}-${index}`} className="rounded-[1.25rem] border border-slate-100 bg-slate-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              <span>{toLabel(row.commentTheme, "Sem tema")}</span>
              <span>•</span>
              <span>{toLabel(row.commentSentiment, "Sem sentimento")}</span>
              <span>•</span>
              <span>Nota {toLabel(row.scoreValue, "-")}</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-700">{toLabel(row.freeTextComment, "Sem comentário")}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function LoadingState() {
  return (
    <Card className="rounded-[1.75rem] border border-slate-200 p-8 shadow-sm">
      <div className="flex items-center gap-3 text-slate-500">
        <HeartPulse className="h-5 w-5" />
        <p className="text-sm font-medium">Consolidando os painéis analíticos do módulo APS...</p>
      </div>
    </Card>
  );
}

export default function ApsModulePanels({
  activeSectionId,
  data,
  accentColor,
  moduleLabel,
  isLoading = false,
}: ApsModulePanelsProps) {
  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      {activeSectionId === "producao" ? (
        <MetricLineChart
          title="Evolução da produção"
          description="Atendimentos concluídos por mês, espelhando o eixo central de produção do material de referência enviado para APS."
          data={data?.production?.monthlySeries ?? []}
          labelKey="periodLabel"
          valueKey="encountersTotal"
          accentColor={accentColor}
        />
      ) : null}

      <ExecutiveCards data={data?.executive} accentColor={accentColor} />

      {activeSectionId === "avaliacao-global" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <MetricLineChart
            title={`Leitura global consolidada de ${moduleLabel}`}
            description="Curva mensal consolidada para o admin comparar o comportamento do módulo antes de aprofundar por canal ou empresa."
            data={data?.production?.monthlySeries ?? []}
            labelKey="periodLabel"
            valueKey="encountersTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Canais com maior conversão na busca ativa"
            description="Comparação entre tentativas, contatos efetivos e agendamentos para leitura global de eficiência operacional."
            data={data?.activeSearch?.channels ?? []}
            labelKey="contactChannel"
            dataKey="attemptsTotal"
            secondaryKey="scheduledTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Especialidades mais acionadas"
            description="Visão agregada das especialidades com maior produção e pressão de encaminhamento no recorte global."
            data={data?.production?.specialties ?? []}
            labelKey="specialtyName"
            dataKey="encountersTotal"
            secondaryKey="referralTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Risco prioritário consolidado"
            description="Comparativo agregado das faixas de risco e da cobertura de plano de cuidado para apoio à leitura executiva do admin."
            data={data?.populationHealth?.riskDistribution ?? []}
            labelKey="riskLabel"
            dataKey="totalLives"
            secondaryKey="carePlanSentTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "visao-geral" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <ExecutiveAreaTrendChart
            title={`Produção e cobertura clínica de ${moduleLabel}`}
            description="Leitura principal do módulo combinando volume assistencial e pessoas efetivamente alcançadas, em uma peça mais densa e menos repetitiva."
            data={data?.production?.monthlySeries ?? []}
            labelKey="periodLabel"
            primaryKey="encountersTotal"
            primaryLabel="Atendimentos"
            secondaryKey="uniquePatients"
            secondaryLabel="Pacientes únicos"
            accentColor={accentColor}
          />
          <AgePyramidPanel
            title="Pirâmide etária da carteira"
            description="Distribuição etária tratada como peça executiva principal, com leitura percentual por faixa e composição complementar da base."
            ageBands={data?.population?.ageBands ?? []}
            ageSexBands={data?.population?.ageSexBands ?? []}
            sexMix={data?.population?.sexMix ?? []}
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Risco e estratificação"
            description="Distribuição de pessoas por intensidade de risco e cobertura de plano de cuidado no recorte selecionado."
            data={data?.populationHealth?.riskDistribution ?? []}
            labelKey="riskLabel"
            dataKey="totalLives"
            secondaryKey="carePlanSentTotal"
            accentColor={accentColor}
          />
          <FunnelJourneyPanel
            title="Jornada até o atendimento"
            description="Fluxo principal de acesso exibido como percurso operacional, destacando retenção e perda de tração entre as etapas."
            data={data?.production?.funnel ?? []}
            labelKey="stageLabel"
            valueKey="stageValue"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "populacao" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <div className="xl:col-span-2">
            <AgePyramidPanel
              title="Pirâmide etária da carteira"
              description="Leitura demográfica central do módulo, com protagonismo visual real para as faixas etárias e reforço complementar da composição da base."
              ageBands={data?.population?.ageBands ?? []}
              ageSexBands={data?.population?.ageSexBands ?? []}
              sexMix={data?.population?.sexMix ?? []}
              accentColor={accentColor}
            />
          </div>
          <MetricPieChart
            title="Distribuição por sexo"
            description="Composição demográfica da base elegível em linguagem executiva, sem perder a governança do escopo do usuário."
            data={data?.population?.sexMix ?? []}
            labelKey="sexLabel"
            valueKey="totalLives"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Estratificação de risco da carteira"
            description="Leitura complementar da base por intensidade clínica e cobertura de plano de cuidado para apoiar a priorização populacional."
            data={data?.populationHealth?.riskDistribution ?? []}
            labelKey="riskLabel"
            dataKey="totalLives"
            secondaryKey="carePlanSentTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Fatores de estilo de vida mais incidentes"
            description="Marcadores comportamentais relevantes para contextualizar a carteira sem obrigar o gestor a sair da leitura demográfica."
            data={data?.populationHealth?.lifestyle ?? []}
            labelKey="factorValue"
            dataKey="totalRecords"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "producao" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <FunnelJourneyPanel
            title="Funil de acesso até atendimento"
            description="Acesso, agendamento e realização apresentados como jornada contínua, favorecendo leitura imediata de retenção entre as etapas."
            data={data?.production?.funnel ?? []}
            labelKey="stageLabel"
            valueKey="stageValue"
            accentColor={accentColor}
          />
          <SpecialtyRankingPanel
            title="Ranking de especialidades"
            description="Volume assistencial e encaminhamentos exibidos em leitura horizontal para destacar hierarquia operacional e pressão de saída clínica."
            data={data?.production?.specialties ?? []}
            labelKey="specialtyName"
            dataKey="encountersTotal"
            secondaryKey="referralTotal"
            accentColor={accentColor}
          />
          <ExecutiveAreaTrendChart
            title="Pacientes únicos e recorrência assistencial"
            description="Combina amplitude de cobertura com revisitas para mostrar, em uma só peça, alcance clínico e continuidade do cuidado."
            data={data?.production?.monthlySeries ?? []}
            labelKey="periodLabel"
            primaryKey="uniquePatients"
            primaryLabel="Pacientes únicos"
            secondaryKey="returnVisits"
            secondaryLabel="Retornos"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Encaminhamentos por especialidade"
            description="Complemento gerencial para observar onde a APS resolve dentro do próprio módulo e onde ainda depende de encaminhamento."
            data={data?.production?.specialties ?? []}
            labelKey="specialtyName"
            dataKey="referralTotal"
            secondaryKey="encountersTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "busca-ativa" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <MetricBarChart
            title="Canais da busca ativa"
            description="Comparação entre tentativas, contatos efetivos e agendamentos por canal operacional da busca ativa."
            data={data?.activeSearch?.channels ?? []}
            labelKey="contactChannel"
            dataKey="attemptsTotal"
            secondaryKey="scheduledTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Conversão por canal"
            description="Leitura complementar entre sucesso de contato e agenda gerada, útil para priorizar os canais com maior produtividade assistencial."
            data={data?.activeSearch?.channels ?? []}
            labelKey="contactChannel"
            dataKey="successTotal"
            secondaryKey="scheduledTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Campanhas e segmentos"
            description="Campanhas priorizadas no ciclo com segmentação assistencial e leitura direta de esforço e conversão."
            data={data?.activeSearch?.campaigns ?? []}
            labelKey="segmentLabel"
            dataKey="successTotal"
            secondaryKey="scheduledTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Esforço por campanha"
            description="Comparação entre volume de tentativas e contatos bem-sucedidos por campanha para evidenciar onde o funil perde tração."
            data={data?.activeSearch?.campaigns ?? []}
            labelKey="campaignName"
            dataKey="attemptsTotal"
            secondaryKey="successTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "monitoramento" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <MetricBarChart
            title="Desfechos do monitoramento PS e tele"
            description="Resultado operacional após o evento agudo, destacando aceites, orientações e resistência à continuidade do cuidado."
            data={data?.monitoring?.outcomes ?? []}
            labelKey="monitoringOutcome"
            dataKey="totalEvents"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Hospitais e prestadores recorrentes"
            description="Prestadores mais frequentes no histórico de eventos monitorados, com apoio à priorização de intervenção."
            data={data?.monitoring?.hospitals ?? []}
            labelKey="hospitalName"
            dataKey="totalEvents"
            secondaryKey="contactSuccessTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "saude-populacional" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <MetricBarChart
            title="Fatores de estilo de vida"
            description="Leitura consolidada de fatores comportamentais e clínicos, sem duplicar entidades já existentes na fundação do HML."
            data={data?.populationHealth?.lifestyle ?? []}
            labelKey="factorValue"
            dataKey="totalRecords"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Rastreamentos preventivos"
            description="Status dos rastreamentos e continuidade assistencial dos casos alterados ou ainda pendentes de seguimento."
            data={data?.populationHealth?.screenings ?? []}
            labelKey="screeningStatus"
            dataKey="totalRecords"
            secondaryKey="followupCompletedTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Linhas de cuidado"
            description="Marcadores clínicos por linha priorizada, com destaque para atingimento de meta e mapeamento de risco."
            data={data?.populationHealth?.careLines ?? []}
            labelKey="careLineType"
            dataKey="measurementsTotal"
            secondaryKey="onTargetTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Distribuição de risco"
            description="Estratificação clínica sintetizada para orientar o que deve ser priorizado por contrato, empresa e escopo de operação."
            data={data?.populationHealth?.riskDistribution ?? []}
            labelKey="riskLabel"
            dataKey="totalLives"
            secondaryKey="carePlanSentTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {activeSectionId === "experiencia" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <MetricPieChart
            title="Distribuição NPS"
            description="Quebra executiva entre promotores, neutros e detratores para leitura rápida da percepção do beneficiário."
            data={data?.experience?.distribution ?? []}
            labelKey="scoreBand"
            valueKey="totalResponses"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Temas e sentimentos"
            description="Classificação qualitativa das respostas abertas já preparada para uso em insights e priorização operacional."
            data={data?.experience?.themes ?? []}
            labelKey="commentTheme"
            dataKey="totalComments"
            accentColor={accentColor}
          />
          <div className="xl:col-span-2">
            <TestimonialList testimonials={data?.experience?.testimonials ?? []} />
          </div>
        </div>
      ) : null}

      {activeSectionId === "acoes-coletivas" ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <MetricLineChart
            title="Ações coletivas por mês"
            description="Leitura de mobilização, inscritos e presença ao longo dos ciclos executados pelo módulo."
            data={data?.collectiveActions?.monthlySeries ?? []}
            labelKey="periodLabel"
            valueKey="attendedTotal"
            accentColor={accentColor}
          />
          <MetricLineChart
            title="Inscritos por ciclo"
            description="Série de inscritos nas ações coletivas para comparar atração versus presença efetiva e apoiar decisões de engajamento."
            data={data?.collectiveActions?.monthlySeries ?? []}
            labelKey="periodLabel"
            valueKey="enrolledTotal"
            accentColor={accentColor}
          />
          <MetricBarChart
            title="Canais das ações coletivas"
            description="Distribuição de participação por canal operacional adotado nas ações coletivas do período selecionado."
            data={data?.collectiveActions?.channels ?? []}
            labelKey="actionChannel"
            dataKey="attendedTotal"
            secondaryKey="actionsTotal"
            accentColor={accentColor}
          />
        </div>
      ) : null}

      {!['avaliacao-global', 'visao-geral', 'populacao', 'producao', 'busca-ativa', 'monitoramento', 'saude-populacional', 'experiencia', 'acoes-coletivas'].includes(activeSectionId) ? (
        <EmptyPanel title="Submenu em preparação" description="Este bloco já está previsto na arquitetura do módulo APS e receberá novos refinamentos conforme a homologação evoluir." />
      ) : null}
    </div>
  );
}
